import { useState, useRef, FormEvent } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qr29snlsngrm4lm4e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const faqData = [
    {
      question: '如何注册海角论坛账号？',
      answer: '访问海角论坛官网，点击右上角"注册/登录"按钮，使用手机号或邮箱即可完成注册。注册后建议完善个人资料并设置头像，以获得更好的社区体验。',
    },
    {
      question: '海角论坛有哪些核心功能板块？',
      answer: '海角论坛设有技术交流、生活分享、数码产品、游戏娱乐、职场教育、汽车出行、健康养生、财经投资八大核心板块，用户可以根据兴趣选择关注。',
    },
    {
      question: '如何在海角论坛发布帖子？',
      answer: '登录账号后，进入感兴趣的板块，点击"发帖"按钮，选择帖子分类，填写标题和内容后即可发布。建议在发布前仔细阅读板块规则，确保内容符合社区规范。',
    },
    {
      question: '海角论坛App如何下载？',
      answer: '目前海角论坛App可通过官方应用商店搜索"海角论坛"下载。支持iOS和Android系统，让您随时随地畅享社区互动体验。',
    },
    {
      question: '海角论坛的内容审核标准是什么？',
      answer: '海角论坛秉持开放包容但严格规范的原则，禁止发布违法违规、侵权、虚假、低俗等内容。所有帖子发布后需经过系统自动审核和人工抽检，确保社区内容质量。',
    },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <section className="py-14 bg-background-50">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">联系我们</h2>
            <p className="mt-3 text-foreground-600 max-w-xl mx-auto">
              如有任何问题或建议，欢迎通过以下方式与我们取得联系
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-3">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">contact@haijiao.com</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-3">
                    <i className="ri-customer-service-2-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-600">400-888-XXXX</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-3">
                    <i className="ri-wechat-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-600">海角论坛官方</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-3">
                    <i className="ri-time-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                <h3 className="text-base font-semibold text-foreground-900 mb-4 font-heading">常见问题</h3>
                <div className="space-y-3">
                  {faqData.map((faq, idx) => (
                    <details key={idx} className="group">
                      <summary className="text-sm text-foreground-800 cursor-pointer hover:text-primary-600 transition-colors py-1.5 font-medium">
                        {faq.question}
                      </summary>
                      <p className="text-xs text-foreground-600 mt-2 pl-2 border-l-2 border-primary-200 leading-relaxed">
                        {faq.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-5 font-heading">发送消息</h3>
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder-foreground-400"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder-foreground-400"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <select
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900"
                      >
                        <option value="">请选择咨询类型</option>
                        <option value="account">账号问题</option>
                        <option value="content">内容反馈</option>
                        <option value="cooperation">商务合作</option>
                        <option value="suggestion">意见建议</option>
                        <option value="other">其他问题</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder-foreground-400 resize-none"
                        placeholder="请输入您想咨询的内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>提交中...
                        </span>
                      ) : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 font-medium text-center">消息已发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 font-medium text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}